import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography } from "@altimateai/lego";
import {
  getAdeBenchRuns,
  getAdeBenchFilterOptions,
  getAdeBenchTaskProgress,
} from "../services/adeBench";
import { Loader } from "../components/Loader";
import { SortColumn, SortDirection } from "./components/SortHeader";
import { ProgressStats } from "./components/ProgressStats";
import { RunsTable } from "./components/RunsTable";
import { DateFilters } from "./components/DateFilters";
import { Pagination } from "./components/Pagination";
import { useRunsFiltering } from "./hooks/useRunsFiltering";

const PAGE_SIZE = 50;

export const AdeBench: React.FC = () => {
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(new Set());
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("start_time");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [offset, setOffset] = useState(0);

  // Date filters
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  };

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.start);
  const [endDate, setEndDate] = useState<string>(defaultDates.end);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(7); // Default to 7 days

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: ["adeBenchFilters"],
    queryFn: getAdeBenchFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch runs
  const {
    data: runsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["adeBenchRuns", offset, selectedAgents, selectedModels, startDate, endDate],
    queryFn: () =>
      getAdeBenchRuns({
        limit: PAGE_SIZE,
        offset,
        agent: selectedAgents.length === 1 ? selectedAgents[0] : undefined,
        model: selectedModels.length === 1 ? selectedModels[0] : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      }),
  });

  const allRuns = runsData?.runs ?? [];

  const filteredRuns = useRunsFiltering({
    runs: allRuns,
    selectedAgents,
    selectedModels,
    sortColumn,
    sortDirection,
  });

  // Auto-select first run when data loads and nothing is selected
  const firstRunId = filteredRuns.length > 0 ? filteredRuns[0].run_id : null;
  React.useEffect(() => {
    if (firstRunId && selectedRunIds.size === 0) {
      setSelectedRunIds(new Set([firstRunId]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRunId]);

  // Determine which run IDs to use for progress stats
  const progressRunIds = useMemo(() => {
    return Array.from(selectedRunIds);
  }, [selectedRunIds]);

  // Fetch progress data based on selected runs
  const {
    data: progressData,
    isFetching: progressLoading,
    refetch: refetchProgress,
  } = useQuery({
    queryKey: ["adeBenchProgress", progressRunIds],
    queryFn: () =>
      getAdeBenchTaskProgress({
        run_ids: progressRunIds.length > 0 ? progressRunIds : undefined,
      }),
    staleTime: 30 * 1000,
    enabled: progressRunIds.length > 0,
  });

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const handleRunClick = (runId: string) => {
    setExpandedRunId(expandedRunId === runId ? null : runId);
  };

  const handleRunSelect = (runId: string, checked: boolean) => {
    const newSelected = new Set(selectedRunIds);
    if (checked) {
      newSelected.add(runId);
    } else {
      newSelected.delete(runId);
    }
    setSelectedRunIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRunIds(new Set(filteredRuns.map(r => r.run_id)));
    } else {
      setSelectedRunIds(new Set());
    }
  };

  const setPresetDateRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
    setSelectedPreset(days);
    setOffset(0);
  };

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - PAGE_SIZE));
  };

  const handleNextPage = () => {
    if (runsData?.has_more) {
      setOffset(offset + PAGE_SIZE);
    }
  };

  const failingCount = progressData?.tasks.filter(t => t.status === "failing").length ?? 0;
  const partialCount = progressData?.tasks.filter(t => t.status === "partial").length ?? 0;
  const solvedCount = progressData?.tasks.filter(t => t.status === "solved").length ?? 0;

  const allSelected = filteredRuns.length > 0 && selectedRunIds.size === filteredRuns.length;

  return (
    <div className="al-p-6 al-max-w-full al-mx-auto">
      <Typography variant="h3" className="al-mb-4">
        ADE-Bench Results
      </Typography>

      <DateFilters
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onStartDateChange={date => {
          setStartDate(date);
          setSelectedPreset(null);
          setOffset(0);
        }}
        onEndDateChange={date => {
          setEndDate(date);
          setSelectedPreset(null);
          setOffset(0);
        }}
        onPresetSelect={setPresetDateRange}
        onRefresh={() => {
          refetch();
          refetchProgress();
        }}
      />

      {/* Progress Stats */}
      <ProgressStats
        progressData={progressData}
        progressLoading={progressLoading}
        selectedRunIds={selectedRunIds}
        failingCount={failingCount}
        partialCount={partialCount}
        solvedCount={solvedCount}
      />

      {isLoading ? (
        <Loader label="Loading benchmark runs..." />
      ) : (
        <div className="al-space-y-4">
          <RunsTable
            filteredRuns={filteredRuns}
            expandedRunId={expandedRunId}
            selectedRunIds={selectedRunIds}
            filterOptions={filterOptions}
            selectedAgents={selectedAgents}
            selectedModels={selectedModels}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            allSelected={allSelected}
            onRunClick={handleRunClick}
            onRunSelect={handleRunSelect}
            onSelectAll={handleSelectAll}
            onAgentsChange={vals => {
              setSelectedAgents(vals);
              setOffset(0);
            }}
            onModelsChange={vals => {
              setSelectedModels(vals);
              setOffset(0);
            }}
            onSort={handleSort}
          />

          <Pagination
            showing={filteredRuns.length}
            total={runsData?.total}
            offset={offset}
            hasMore={runsData?.has_more}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
          />
        </div>
      )}
    </div>
  );
};
